import { sendTrackEvent } from '@edx/frontend-platform/analytics';
import { getConfig } from '@edx/frontend-platform/config';
import { logError } from '@edx/frontend-platform/logging';

import { PLAN_TYPE } from '@/constants/events';
import { useCheckoutAttributionStore, useCheckoutStartedStore } from '@/hooks/checkoutSessionStorage';

import type { CheckoutEventStep } from '@/constants/events';
import type { CheckoutAttributionProperties } from '@/hooks/checkoutSessionStorage';

// Helpers for the checkout Segment events (ENT-12328). Tracking must never interrupt checkout,
// so storage and emission failures are logged and swallowed (via the persisted stores in
// '@/hooks/checkoutSessionStorage', which wrap sessionStorage access in one shared safe adapter).

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

export interface CheckoutProductProperties {
  product_id?: string;
  sku?: number;
  category: 'subscription';
  name: 'teams' | 'essentials';
  brand: 'enterprise';
  variant?: string;
  price?: number;
  slug?: string;
  payment_schedule: 'yearly';
}

export interface CheckoutOrderProperties {
  order_id?: string;
  payment_method?: string;
  total_quantity?: number;
  revenue?: number;
}

export const isCheckoutEventsV2Enabled = (): boolean => {
  const { FEATURE_SSP_CHECKOUT_SEGMENT_EVENTS_V2 } = getConfig();
  return FEATURE_SSP_CHECKOUT_SEGMENT_EVENTS_V2 === true || FEATURE_SSP_CHECKOUT_SEGMENT_EVENTS_V2 === 'true';
};

/** Drops undefined, null and empty-string values so they are omitted, not sent empty. */
export const omitEmptyProperties = <T extends Record<string, unknown>>(properties: T): Partial<T> => (
  Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  ) as Partial<T>
);

/** Stores UTM params and referrer from the checkout landing; a later URL with UTMs replaces them. */
export const captureCheckoutAttribution = (url: string = window.location.href): void => {
  const { searchParams } = new URL(url);
  const utmProperties = omitEmptyProperties(
    Object.fromEntries(UTM_PARAMS.map((param) => [param, searchParams.get(param)])),
  );
  const hasUtmParams = Object.keys(utmProperties).length > 0;
  const { captured, setAttribution } = useCheckoutAttributionStore.getState();
  if (captured && !hasUtmParams) {
    return;
  }
  setAttribution(omitEmptyProperties({ ...utmProperties, referrer: document.referrer }));
};

export const getCheckoutAttribution = (): CheckoutAttributionProperties => (
  useCheckoutAttributionStore.getState().attribution
);

/** Maps the selected price and SSP product to event product properties; "academy" slugs are Essentials. */
export const buildCheckoutProductProperties = ({
  price,
  selectedProduct,
}: {
  price: CheckoutContextPrice | null | undefined;
  selectedProduct?: { name?: string } | null;
}): CheckoutProductProperties => {
  const slug = price?.sspProductSlug;
  return omitEmptyProperties({
    product_id: price?.product,
    sku: price?.catalogQueryId,
    category: 'subscription',
    name: slug?.includes('academy') ? PLAN_TYPE.ESSENTIALS : PLAN_TYPE.TEAMS,
    brand: 'enterprise',
    variant: selectedProduct?.name?.trim(),
    price: price?.unitAmount != null ? price.unitAmount / 100 : undefined,
    slug,
    // Only yearly plans are sold today.
    payment_schedule: 'yearly',
  }) as CheckoutProductProperties;
};

/**
 * Maps the paid Stripe Checkout session and checkout intent to event order properties.
 * `revenue` is Stripe's charged total, not price × quantity. `payment_method` is omitted because
 * Stripe Checkout doesn't expose it client-side.
 */
export const buildCheckoutOrderProperties = ({
  checkoutIntent,
  session,
}: {
  checkoutIntent: Pick<CheckoutContextCheckoutIntent, 'quantity'> | null | undefined;
  session: {
    id?: string;
    minorUnitsAmountDivisor?: number;
    total?: { total?: { minorUnitsAmount?: number } };
  } | null | undefined;
}): CheckoutOrderProperties => {
  const chargedMinorUnits = session?.total?.total?.minorUnitsAmount;
  return omitEmptyProperties({
    order_id: session?.id,
    total_quantity: checkoutIntent?.quantity,
    revenue: chargedMinorUnits != null ? chargedMinorUnits / (session?.minorUnitsAmountDivisor || 100) : undefined,
  });
};

/** Sends a checkout event. No-op when the flag is off; never throws. */
export const sendCheckoutEvent = ({
  eventName,
  product,
  step,
  order,
}: {
  eventName: string;
  product: CheckoutProductProperties;
  step?: CheckoutEventStep;
  order?: CheckoutOrderProperties;
}): void => {
  if (!isCheckoutEventsV2Enabled()) {
    return;
  }
  try {
    sendTrackEvent(eventName, omitEmptyProperties({
      ...product, ...step, ...order, ...getCheckoutAttribution(),
    }));
  } catch (error) {
    logError(`Failed to send checkout event ${eventName}`, error);
  }
};

/** True only on the first call in a browser session, so checkout_started fires once. */
export const claimCheckoutStarted = (): boolean => useCheckoutStartedStore.getState().claimStarted();
